import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useClinicRules } from '../../hooks/useClinicRules';
import { Shield, ShieldCheck, Sparkles, ChevronDown, RotateCcw, X, Plus } from 'lucide-react';

// Rule metadata: descriptions, trigger conditions, and impact
// Rewritten with plain-language "When" / "Result" copy
const RULE_METADATA = {
  escalate_no_response: {
    description: 'Move patients back into the follow-up queue if they haven\'t responded after multiple outreach attempts.',
    trigger: 'A patient hasn\'t replied after the configured number of outreach attempts.',
    impact: 'These patients reappear in your Triage list so no one falls through the cracks.',
    hasConfig: true,
  },
  missed_visits_flag: {
    description: 'Prioritize patients who have missed multiple appointments and may be at risk of disengaging from care.',
    trigger: 'A patient\'s missed-visit count reaches the threshold you set.',
    impact: 'They move to the top of your Triage list for immediate outreach.',
    hasConfig: true,
  },
  flag_urgent_status: {
    description: 'Automatically surface patients marked as urgent so they receive timely follow-up.',
    trigger: 'A patient\'s status is set to "urgent" in the EHR.',
    impact: 'They appear at the very top of your Triage list.',
    hasConfig: false,
  },
  flag_concerning_status: {
    description: 'Prioritize patients whose status indicates they need attention, such as "callback", "no show", or "overdue".',
    trigger: 'A patient\'s status matches a concerning keyword like "callback" or "no show".',
    impact: 'They are bumped up in your Triage list so staff sees them sooner.',
    hasConfig: false,
  },
  overdue_appointment: {
    description: 'Flag patients who haven\'t been seen in a while based on days since their last visit.',
    trigger: 'Days since a patient\'s last visit exceed the thresholds you configure.',
    impact: 'They are flagged as high or medium risk in your Triage list.',
    hasConfig: true,
  },
  med_adherence_risk: {
    description: 'Flag patients on Medicare with specialty drugs who are above the configured age threshold.',
    trigger: 'A patient matches on Medicare insurance, a specialty drug, and age above your threshold.',
    impact: 'All 3 criteria → high risk. 2 of 3 → medium risk. Surfaces them in Triage.',
    hasConfig: true,
  },
  business_hours_only: {
    description: 'Send automated messages only during approved business hours.',
    trigger: 'An automated message is about to be sent outside the hours you set.',
    impact: 'The message is held and delivered in the next business-hours window.',
    hasConfig: true,
  },
  max_messages_per_week: {
    description: 'Limit how many messages a patient can receive in a given time period.',
    trigger: 'A patient has already received the maximum number of messages this week.',
    impact: 'Prevents patients from feeling overwhelmed. Extra messages wait until next week.',
    hasConfig: true,
  },
  min_days_between_messages: {
    description: 'Require a minimum number of days between messages sent to the same patient.',
    trigger: 'A follow-up message is scheduled too soon after the last one.',
    impact: 'The message is delayed so patients aren\'t contacted back-to-back.',
    hasConfig: true,
  },
};

// Inline config summary shown on collapsed rule rows
const RULE_SUMMARY = (rule) => {
  const c = rule.config || {};
  switch (rule.code) {
    case 'escalate_no_response':
      return `After ${c.attempts || 3} attempts`;
    case 'missed_visits_flag':
      return `Threshold: ${c.threshold || 3} missed`;
    case 'flag_urgent_status':
      return 'Automatic';
    case 'flag_concerning_status':
      return 'Automatic';
    case 'overdue_appointment':
      return `High: ${c.high_days || 90}d / Medium: ${c.medium_days || 30}d`;
    case 'med_adherence_risk':
      return `Age ≥ ${c.age_threshold || 65} · ${(c.drug_list || []).length} drugs`;
    case 'business_hours_only':
      return `${c.start_hour ?? 9}:00\u201317:00`;
    case 'max_messages_per_week':
      return `Max ${c.max_count || 3}/week`;
    case 'min_days_between_messages':
      return `${c.days ?? 1} day gap`;
    default:
      return null;
  }
};

// Rule category configuration
const CATEGORY_CONFIG = {
  risk: {
    icon: Shield,
    label: 'Risk & Triage',
    description: 'Control which patients surface at the top of your Triage list',
    iconColor: 'text-rose-500',
    iconBg: 'bg-rose-50',
    pillText: 'text-rose-600',
    pillBg: 'bg-rose-50',
  },
  campaign: {
    icon: ShieldCheck,
    label: 'Delivery Guardrails',
    description: 'Protect patients and your reputation with safe messaging limits',
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    pillText: 'text-blue-600',
    pillBg: 'bg-blue-50',
  },
};

export default function RulesSettings() {
  const { loading, error, updateRule, resetRuleToDefault, getRulesByCategory } = useClinicRules();
  const [expandedRules, setExpandedRules] = useState({});
  const [savingRules, setSavingRules] = useState({});

  const riskRules = getRulesByCategory('risk');
  const campaignRules = getRulesByCategory('campaign');

  const toggleExpand = (ruleId) => {
    setExpandedRules(prev => ({ ...prev, [ruleId]: !prev[ruleId] }));
  };

  const handleToggleEnabled = async (rule) => {
    setSavingRules(prev => ({ ...prev, [rule.id]: true }));
    const { success, error } = await updateRule(rule.id, { enabled: !rule.enabled });
    setSavingRules(prev => ({ ...prev, [rule.id]: false }));

    if (success) {
      toast.success(`${rule.name} ${rule.enabled ? 'disabled' : 'enabled'}`);
    } else {
      toast.error(error?.message || 'Failed to update rule');
    }
  };

  const handleConfigSave = async (rule, newConfig) => {
    setSavingRules(prev => ({ ...prev, [rule.id]: true }));
    const { success, error } = await updateRule(rule.id, { config: newConfig });
    setSavingRules(prev => ({ ...prev, [rule.id]: false }));

    if (success) {
      toast.success('Configuration saved');
    } else {
      toast.error(error?.message || 'Failed to save configuration');
    }
  };

  const handleResetToDefault = async (rule) => {
    setSavingRules(prev => ({ ...prev, [rule.id]: true }));
    const { success, error } = await resetRuleToDefault(rule.id);
    setSavingRules(prev => ({ ...prev, [rule.id]: false }));

    if (success) {
      toast.success('Reset to default');
    } else {
      toast.error(error?.message || 'Failed to reset');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg" />
              <div className="space-y-2">
                <div className="h-4 w-32 bg-slate-100 rounded" />
                <div className="h-3 w-48 bg-slate-100 rounded" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="h-12 bg-slate-50 rounded-lg" />
              <div className="h-12 bg-slate-50 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-center gap-3 text-red-600">
          <Shield className="h-5 w-5" />
          <p className="font-medium">Failed to load rules</p>
        </div>
        <p className="mt-2 text-sm text-red-500">{error}</p>
      </div>
    );
  }

  // Count active rules per category (+1 for always-on EHR Signal Detection in risk)
  const riskActiveCount = riskRules.filter(r => r.enabled).length + 1;
  const riskTotalCount = riskRules.length + 1;
  const campaignActiveCount = campaignRules.filter(r => r.enabled).length;
  const campaignTotalCount = campaignRules.length;

  return (
    <div className="space-y-6">
      <RuleCategory
        config={CATEGORY_CONFIG.risk}
        rules={riskRules}
        activeCount={riskActiveCount}
        totalCount={riskTotalCount}
        alwaysOnContent={
          <div className="px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-blue-100">
                <Sparkles className="h-4 w-4 text-blue-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900">EHR Signal Detection</p>
                <p className="text-sm text-slate-500">
                  We detect flags like "Overdue PSA" and statuses like "callback", "no show"
                </p>
              </div>
              <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md shrink-0">
                Always On
              </span>
            </div>
          </div>
        }
        expandedRules={expandedRules}
        savingRules={savingRules}
        onToggleExpand={toggleExpand}
        onToggleEnabled={handleToggleEnabled}
        onConfigSave={handleConfigSave}
        onResetToDefault={handleResetToDefault}
      />

      <RuleCategory
        config={CATEGORY_CONFIG.campaign}
        rules={campaignRules}
        activeCount={campaignActiveCount}
        totalCount={campaignTotalCount}
        expandedRules={expandedRules}
        savingRules={savingRules}
        onToggleExpand={toggleExpand}
        onToggleEnabled={handleToggleEnabled}
        onConfigSave={handleConfigSave}
        onResetToDefault={handleResetToDefault}
      />
    </div>
  );
}

function RuleCategory({
  config,
  rules,
  activeCount,
  totalCount,
  expandedRules,
  savingRules,
  onToggleExpand,
  onToggleEnabled,
  onConfigSave,
  onResetToDefault,
  alwaysOnContent,
}) {
  const Icon = config.icon;

  if (rules.length === 0 && !alwaysOnContent) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Category Header */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${config.iconBg}`}>
            <Icon className={`h-5 w-5 ${config.iconColor}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-slate-900">{config.label}</h3>
              <span className={`text-xs font-medium ${config.pillText} ${config.pillBg} px-2 py-0.5 rounded-full`}>
                {activeCount}/{totalCount} active
              </span>
            </div>
            <p className="text-sm text-slate-500">{config.description}</p>
          </div>
        </div>
      </div>

      {/* Rules List */}
      <div className="divide-y divide-slate-100">
        {alwaysOnContent}
        {rules.map(rule => (
          <RuleItem
            key={rule.id}
            rule={rule}
            expanded={expandedRules[rule.id]}
            saving={savingRules[rule.id]}
            onToggleExpand={() => onToggleExpand(rule.id)}
            onToggleEnabled={() => onToggleEnabled(rule)}
            onConfigSave={(config) => onConfigSave(rule, config)}
            onResetToDefault={() => onResetToDefault(rule)}
          />
        ))}
      </div>
    </div>
  );
}

function RuleItem({
  rule,
  expanded,
  saving,
  onToggleExpand,
  onToggleEnabled,
  onConfigSave,
  onResetToDefault,
}) {
  const metadata = RULE_METADATA[rule.code] || {};
  // Use metadata description as fallback for consistent microcopy
  const description = metadata.description || rule.description;
  const summary = RULE_SUMMARY(rule);
  const disabled = !rule.enabled;

  return (
    <div className="px-5 py-4">
      <div className={`flex items-start gap-4 ${disabled ? 'opacity-60' : ''}`}>
        {/* Rule Info */}
        <button
          onClick={onToggleExpand}
          className="flex-1 text-left min-w-0 pt-0.5"
        >
          <div className="flex items-center gap-2">
            <p className={`font-medium ${disabled ? 'text-slate-400' : 'text-slate-900'}`}>{rule.name}</p>
            {disabled && (
              <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                Off
              </span>
            )}
          </div>
          <p className={`text-sm ${disabled ? 'text-slate-400' : 'text-slate-500'}`}>{description}</p>
          {!expanded && summary && (
            <p className="text-xs text-slate-400 mt-0.5">{summary}</p>
          )}
        </button>

        {/* Toggle + Action — fixed-width column so they align across rows */}
        <div className="flex items-center gap-3 shrink-0 pt-0.5">
          <button
            onClick={onToggleEnabled}
            disabled={saving}
            className={`
              relative inline-flex h-6 w-11 shrink-0 items-center rounded-full
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2
              ${rule.enabled ? 'bg-slate-900' : 'bg-slate-200'}
              ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            role="switch"
            aria-checked={rule.enabled}
            aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'}
          >
            <span
              className={`
                pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${rule.enabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>

          <button
            onClick={onToggleExpand}
            className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors w-16 text-left"
          >
            {expanded ? (
              <span className="flex items-center gap-1">
                <ChevronDown className="h-3.5 w-3.5" />
                Close
              </span>
            ) : (
              <span>{metadata.hasConfig ? 'Configure \u203A' : 'Details \u203A'}</span>
            )}
          </button>
        </div>
      </div>

      {/* Expanded Config Panel */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* When / Result info box */}
          {(metadata.trigger || metadata.impact) && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
              {metadata.trigger && (
                <div className="flex gap-2 text-sm">
                  <span className="font-medium text-slate-700 shrink-0">When:</span>
                  <span className="text-slate-600">{metadata.trigger}</span>
                </div>
              )}
              {metadata.impact && (
                <div className="flex gap-2 text-sm">
                  <span className="font-medium text-slate-700 shrink-0">Result:</span>
                  <span className="text-slate-600">{metadata.impact}</span>
                </div>
              )}
            </div>
          )}

          <RuleConfigEditor
            rule={rule}
            onSave={onConfigSave}
            onReset={onResetToDefault}
            disabled={saving}
          />
        </div>
      )}
    </div>
  );
}

function RuleConfigEditor({ rule, onSave, onReset, disabled }) {
  const [localConfig, setLocalConfig] = useState(rule.config);

  const handleChange = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    // Cross-field validation for overdue_appointment thresholds
    if (rule.code === 'overdue_appointment') {
      const high = localConfig.high_days;
      const medium = localConfig.medium_days;
      if (high === '' || medium === '' || !Number.isFinite(Number(high)) || !Number.isFinite(Number(medium))) {
        toast.error('Please enter valid values for risk thresholds.');
        return;
      }
      if (Number(high) <= Number(medium)) {
        toast.error('High risk threshold must be greater than medium risk threshold.');
        return;
      }
    }
    onSave(localConfig);
  };

  const hasChanges = JSON.stringify(localConfig) !== JSON.stringify(rule.config);
  const isDefault = JSON.stringify(localConfig) === JSON.stringify(rule.defaultConfig);

  const inputClasses = `
    w-20 px-3 py-2 text-sm border border-slate-200 rounded-lg
    focus:ring-2 focus:ring-slate-400 focus:border-slate-400 outline-none
    disabled:bg-slate-100 disabled:cursor-not-allowed
  `;

  const renderInputs = () => {
    switch (rule.code) {
      case 'missed_visits_flag':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Flag patients when missed visits reach:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="10"
                value={localConfig.threshold || 3}
                onChange={(e) => handleChange('threshold', parseInt(e.target.value) || 1)}
                className={inputClasses}
                disabled={disabled}
              />
              <span className="text-sm text-slate-500">appointments</span>
            </div>
          </div>
        );

      case 'escalate_no_response':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Escalate after outreach attempts:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="10"
                value={localConfig.attempts || 3}
                onChange={(e) => handleChange('attempts', parseInt(e.target.value) || 1)}
                className={inputClasses}
                disabled={disabled}
              />
              <span className="text-sm text-slate-500">attempts with no response</span>
            </div>
          </div>
        );

      case 'flag_urgent_status':
        return (
          <p className="text-sm text-slate-500">
            No additional configuration needed.
          </p>
        );

      case 'flag_concerning_status':
        return (
          <p className="text-sm text-slate-500">
            Automatically detects concerning statuses like "callback", "no show", "overdue", and similar keywords. No additional configuration needed.
          </p>
        );

      case 'overdue_appointment': {
        const highDays = localConfig.high_days;
        const mediumDays = localConfig.medium_days;
        const bothFilled = highDays !== '' && highDays != null && mediumDays !== '' && mediumDays != null;
        const thresholdError = bothFilled && Number(highDays) <= Number(mediumDays);
        return (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Flag patients based on days since last visit:
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={highDays ?? 90}
                  onChange={(e) => handleChange('high_days', e.target.value === '' ? '' : parseInt(e.target.value))}
                  className={`${inputClasses} ${thresholdError ? '!border-red-300' : ''}`}
                  disabled={disabled}
                />
                <span className="text-sm text-red-600 font-medium">days → High risk</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={mediumDays ?? 30}
                  onChange={(e) => handleChange('medium_days', e.target.value === '' ? '' : parseInt(e.target.value))}
                  className={`${inputClasses} ${thresholdError ? '!border-red-300' : ''}`}
                  disabled={disabled}
                />
                <span className="text-sm text-amber-600 font-medium">days → Medium risk</span>
              </div>
            </div>
            {thresholdError && (
              <p className="text-xs text-red-600">High risk days must be greater than medium risk days.</p>
            )}
          </div>
        );
      }

      case 'med_adherence_risk':
        return (
          <MedAdherenceConfig
            localConfig={localConfig}
            onChange={handleChange}
            disabled={disabled}
            inputClasses={inputClasses}
          />
        );

      case 'business_hours_only':
        return (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Send messages only between:
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={localConfig.start_hour ?? 9}
                  onChange={(e) => handleChange('start_hour', parseInt(e.target.value) || 0)}
                  className={inputClasses}
                  disabled={disabled}
                />
                <span className="text-sm text-slate-500">:00</span>
              </div>
              <span className="text-sm text-slate-400">to</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={localConfig.end_hour ?? 17}
                  onChange={(e) => handleChange('end_hour', parseInt(e.target.value) || 0)}
                  className={inputClasses}
                  disabled={disabled}
                />
                <span className="text-sm text-slate-500">:00</span>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              24-hour format (e.g., 9 = 9 AM, 17 = 5 PM)
            </p>
          </div>
        );

      case 'max_messages_per_week':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Maximum messages per patient per week:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="20"
                value={localConfig.max_count || 3}
                onChange={(e) => handleChange('max_count', parseInt(e.target.value) || 1)}
                className={inputClasses}
                disabled={disabled}
              />
              <span className="text-sm text-slate-500">messages</span>
            </div>
          </div>
        );

      case 'min_days_between_messages':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Minimum days between messages to same patient:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="30"
                value={localConfig.days ?? 1}
                onChange={(e) => handleChange('days', parseInt(e.target.value) || 0)}
                className={inputClasses}
                disabled={disabled}
              />
              <span className="text-sm text-slate-500">days</span>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Configuration:</label>
            <pre className="text-xs text-slate-600 bg-white p-3 rounded-lg border border-slate-200 overflow-auto">
              {JSON.stringify(localConfig, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {renderInputs()}

      {(hasChanges || !isDefault) && (
        <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={disabled}
              className="px-3 py-1.5 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors"
            >
              Save Changes
            </button>
          )}
          {!isDefault && (
            <button
              onClick={onReset}
              disabled={disabled}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to default
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MedAdherenceConfig({ localConfig, onChange, disabled, inputClasses }) {
  const [newDrug, setNewDrug] = useState('');
  const drugList = localConfig.drug_list || [];

  const addDrug = () => {
    const trimmed = newDrug.trim().toLowerCase();
    if (trimmed && !drugList.includes(trimmed)) {
      onChange('drug_list', [...drugList, trimmed]);
      setNewDrug('');
    }
  };

  const removeDrug = (drug) => {
    onChange('drug_list', drugList.filter(d => d !== drug));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDrug();
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          Minimum patient age:
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            max="120"
            value={localConfig.age_threshold || 65}
            onChange={(e) => onChange('age_threshold', parseInt(e.target.value) || 65)}
            className={inputClasses}
            disabled={disabled}
          />
          <span className="text-sm text-slate-500">years</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          Specialty drugs ({drugList.length}):
        </label>
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
          {drugList.map((drug) => (
            <span
              key={drug}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-md"
            >
              {drug}
              <button
                onClick={() => removeDrug(drug)}
                disabled={disabled}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Add drug name..."
            value={newDrug}
            onChange={(e) => setNewDrug(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-slate-400 outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
            disabled={disabled}
          />
          <button
            onClick={addDrug}
            disabled={disabled || !newDrug.trim()}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
